import React from "react";
import { CalendarDays, ChevronDown, Plus, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "../ui/popover";
import { InlineDatePickerPanel } from "../DatePickerField";
import {
  formatDisplayDate,
  formatUsd,
  isDepositSettled,
  roundMoney,
  SERVICE_OFFERING_TITLES,
} from "../../data/revenueData";

const FONT = "'Nohemi', 'Space Grotesk', sans-serif";
const BG = "#FCFCFC";
const TEXT = "#222222";
const META = "#9E9B93";
const RULE = "#C8C4BC";
const RULE_SOFT = "#D4D0C8";
const ACCENT = "#001EFF";

function parseDocDate(value) {
  if (!value) return undefined;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

/** Minimal document-meta date control — text + tiny calendar, portal calendar panel below. */
function DocMetaDateField({
  label,
  value,
  expanded,
  onExpandedChange,
  "data-testid": testId,
}) {
  const selected = parseDocDate(value);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        gap: 6,
        lineHeight: 1.45,
      }}
    >
      <span style={{ color: META }}>{label}</span>
      <button
        type="button"
        onClick={() => onExpandedChange?.(!expanded)}
        aria-expanded={expanded}
        data-testid={testId}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          margin: 0,
          padding: "1px 2px",
          border: "none",
          borderRadius: 2,
          background: expanded ? "rgba(0, 30, 255, 0.04)" : "transparent",
          boxShadow: expanded ? `inset 0 0 0 1px ${ACCENT}` : "none",
          color: TEXT,
          fontFamily: FONT,
          fontWeight: 300,
          fontSize: 11,
          lineHeight: 1.4,
          cursor: "pointer",
        }}
      >
        <span>{selected ? format(selected, "MMM d, yyyy") : "Select date"}</span>
        <CalendarDays size={12} strokeWidth={1.4} style={{ color: META, flexShrink: 0 }} />
      </button>
    </div>
  );
}

const fieldBase = {
  fontFamily: FONT,
  fontWeight: 300,
  color: TEXT,
  background: "transparent",
  border: "none",
  borderRadius: 2,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
  padding: "2px 4px",
  margin: "0 -4px",
};

const fieldFocus = {
  boxShadow: `inset 0 0 0 1px ${ACCENT}`,
  background: "rgba(0, 30, 255, 0.03)",
};

function EditableText({
  value,
  onChange,
  placeholder,
  multiline = false,
  style,
  className,
  "data-testid": testId,
}) {
  const [focused, setFocused] = React.useState(false);
  const shared = {
    ...fieldBase,
    ...(focused ? fieldFocus : null),
    ...style,
  };

  if (multiline) {
    return (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        placeholder={placeholder}
        rows={2}
        className={className}
        style={{
          ...shared,
          resize: "vertical",
          minHeight: 40,
          lineHeight: 1.6,
        }}
        data-testid={testId}
      />
    );
  }

  return (
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      className={className}
      style={shared}
      data-testid={testId}
    />
  );
}

/** Combobox: pick a known offering or type a one-off title (not saved to the list). */
function ServiceTitleCombobox({
  value,
  onChange,
  placeholder = "Service title",
  style,
  "data-testid": testId,
}) {
  const [open, setOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const [menuWidth, setMenuWidth] = React.useState(0);
  const inputRef = React.useRef(null);
  const wrapRef = React.useRef(null);

  React.useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const sync = () => setMenuWidth(Math.round(el.getBoundingClientRect().width));
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const query = String(value || "").trim().toLowerCase();
  const options = SERVICE_OFFERING_TITLES.filter((title) => {
    if (!query) return true;
    return title.toLowerCase().includes(query);
  });

  const pick = (title) => {
    onChange(title);
    setOpen(false);
    inputRef.current?.focus();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div
          ref={wrapRef}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            ...fieldBase,
            margin: "0 -4px",
            padding: "2px 4px",
            width: "100%",
            ...(focused || open ? fieldFocus : null),
          }}
        >
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={() => {
              setFocused(true);
              setOpen(true);
            }}
            onBlur={() => setFocused(false)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setOpen(true);
              }
            }}
            placeholder={placeholder}
            style={{
              ...fieldBase,
              margin: 0,
              padding: 0,
              boxShadow: "none",
              background: "transparent",
              flex: 1,
              minWidth: 0,
              ...style,
            }}
            data-testid={testId}
            aria-autocomplete="list"
            aria-expanded={open}
            role="combobox"
          />
          <PopoverTrigger asChild>
            <button
              type="button"
              tabIndex={-1}
              aria-label="Choose a service"
              onMouseDown={(e) => e.preventDefault()}
              style={{
                border: "none",
                background: "transparent",
                color: META,
                cursor: "pointer",
                padding: 2,
                display: "inline-flex",
                flexShrink: 0,
              }}
              data-testid={testId ? `${testId}-toggle` : undefined}
            >
              <ChevronDown size={14} strokeWidth={1.4} />
            </button>
          </PopoverTrigger>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        style={menuWidth ? { width: menuWidth, minWidth: menuWidth, maxWidth: menuWidth } : undefined}
        className="z-[80] border border-[var(--t-border)] bg-[var(--t-bg)] p-1 text-[var(--t-text)] shadow-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid={testId ? `${testId}-menu` : undefined}
      >
        <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.12em] text-[var(--t-meta)]">
          Your services
        </div>
        {options.length === 0 ? (
          <div className="px-3 py-2 text-xs font-light text-[var(--t-meta)]">
            Keep typing for a custom title
          </div>
        ) : (
          options.map((title) => (
            <button
              key={title}
              type="button"
              className="flex w-full items-center rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-sm font-light transition-colors hover:bg-black/[0.04]"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(title)}
              data-testid={testId ? `${testId}-option-${title}` : undefined}
            >
              {title}
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}

function sanitizeMoneyInput(raw) {
  const cleaned = String(raw ?? "").replace(/[^\d.]/g, "");
  const dot = cleaned.indexOf(".");
  if (dot === -1) return cleaned;
  return `${cleaned.slice(0, dot)}.${cleaned.slice(dot + 1).replace(/\./g, "")}`;
}

/**
 * Letter-style editable invoice that mirrors the TAĪSTU PDF layout.
 * Click any field on the document to edit; Save regenerates the client PDF.
 */
export default function InvoiceDocumentEditor({
  invoiceNumber,
  clientName,
  issued,
  due,
  onIssuedChange,
  onDueChange,
  lineItems,
  onLineChange,
  onAddLine,
  onRemoveLine,
  paymentPlan,
  deposits,
}) {
  const subtotal = roundMoney(
    (lineItems || []).reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
  );
  const showSchedule =
    paymentPlan === "partial" && Array.isArray(deposits) && deposits.length >= 2;
  const collected = roundMoney(
    (deposits || []).reduce((sum, d) => sum + (Number(d.paid_amount) || 0), 0),
  );
  const balanceDue = roundMoney(Math.max(0, subtotal - collected));
  const isFullyPaid = subtotal > 0 && collected + 0.001 >= subtotal;
  const paidAtDisplay = React.useMemo(() => {
    if (!isFullyPaid) return null;
    const dates = (deposits || [])
      .filter((d) => isDepositSettled(d) && d.paid_at)
      .map((d) => String(d.paid_at).slice(0, 10))
      .filter(Boolean)
      .sort();
    if (!dates.length) return null;
    return formatDisplayDate(dates[dates.length - 1]);
  }, [deposits, isFullyPaid]);
  const [openDateField, setOpenDateField] = React.useState(null);

  function isDepositPaid(dep) {
    return isDepositSettled(dep);
  }

  return (
    <div
      className="invoice-document-editor"
      style={{
        fontFamily: FONT,
        background: BG,
        color: TEXT,
        width: "100%",
        maxWidth: 680,
        minHeight: 880,
        margin: "0 auto",
        padding: "28px 32px 36px",
        boxShadow: "none",
        border: "none",
        boxSizing: "border-box",
      }}
      data-testid="invoice-document-editor"
    >
      {/* Top rule / wordmark */}
      <div
        style={{
          borderTop: `0.5px solid ${RULE}`,
          paddingTop: 10,
          marginBottom: 28,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <span
          style={{
            fontWeight: 300,
            fontSize: 11,
            letterSpacing: "0.08em",
            color: TEXT,
          }}
        >
          TAĪSTU
        </span>
        <div
          style={{
            textAlign: "right",
            fontWeight: 300,
            fontSize: 10,
            lineHeight: 1.7,
            color: TEXT,
          }}
        >
          tai@taistu.com
          <br />
          taistu.com
        </div>
      </div>

      {/* Title + meta */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          marginBottom: 28,
          paddingBottom: 20,
          borderBottom: `0.5px solid ${RULE}`,
          gap: 16,
        }}
      >
        <div
          style={{
            fontWeight: 300,
            fontSize: 42,
            letterSpacing: "-0.01em",
            lineHeight: 1,
            color: TEXT,
          }}
        >
          Invoice
        </div>
        <div
          style={{
            textAlign: "right",
            fontSize: 11,
            color: TEXT,
            lineHeight: 1.55,
            fontWeight: 300,
            minWidth: 180,
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
            gap: 5,
          }}
        >
          <div
            style={{
              fontWeight: 300,
              fontSize: 13,
              color: ACCENT,
              letterSpacing: "0.06em",
            }}
          >
            {invoiceNumber}
          </div>
          <DocMetaDateField
            label="Issued:"
            value={issued || ""}
            expanded={openDateField === "issued"}
            onExpandedChange={(next) => setOpenDateField(next ? "issued" : null)}
            data-testid="invoice-doc-issued"
          />
          <DocMetaDateField
            label="Due:"
            value={due || ""}
            expanded={openDateField === "due"}
            onExpandedChange={(next) => setOpenDateField(next ? "due" : null)}
            data-testid="invoice-doc-due"
          />
          {isFullyPaid && paidAtDisplay ? (
            <div data-testid="invoice-doc-paid-at" style={{ color: ACCENT }}>
              Paid: {paidAtDisplay}
            </div>
          ) : isFullyPaid ? (
            <div data-testid="invoice-doc-paid" style={{ color: ACCENT }}>
              Paid
            </div>
          ) : null}
          <div>Bill to: {clientName}</div>
        </div>
      </div>

      {openDateField === "issued" ? (
        <InlineDatePickerPanel
          value={issued || ""}
          onChange={(next) => {
            onIssuedChange(next);
            setOpenDateField(null);
          }}
          onClose={() => setOpenDateField(null)}
          className="mb-5"
          data-testid="invoice-doc-issued-panel"
        />
      ) : null}
      {openDateField === "due" ? (
        <InlineDatePickerPanel
          value={due || ""}
          onChange={(next) => {
            onDueChange(next);
            setOpenDateField(null);
          }}
          onClose={() => setOpenDateField(null)}
          className="mb-5"
          data-testid="invoice-doc-due-panel"
        />
      ) : null}

      {/* Services */}
      <div
        style={{
          fontWeight: 300,
          fontSize: 9,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: META,
          borderBottom: `0.3px solid ${RULE_SOFT}`,
          paddingBottom: 5,
          marginBottom: 8,
        }}
      >
        Services
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <td
              style={{
                fontWeight: 300,
                fontSize: 9,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: META,
                padding: "8px 0 6px",
                borderBottom: `0.5px solid ${RULE_SOFT}`,
              }}
            />
            <td
              style={{
                fontWeight: 300,
                fontSize: 9,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: META,
                padding: "8px 0 6px",
                borderBottom: `0.5px solid ${RULE_SOFT}`,
                textAlign: "right",
                width: 110,
              }}
            >
              Amount
            </td>
            <td style={{ width: 36, borderBottom: `0.5px solid ${RULE_SOFT}` }} />
          </tr>
        </thead>
        <tbody>
          {(lineItems || []).map((item, index) => (
            <tr key={item.id}>
              <td
                style={{
                  padding: "12px 8px 10px 0",
                  borderBottom: `0.5px solid ${RULE_SOFT}`,
                  verticalAlign: "top",
                }}
              >
                <ServiceTitleCombobox
                  value={item.title}
                  onChange={(v) => onLineChange(item.id, "title", v)}
                  placeholder="Service title"
                  style={{ fontSize: 13, fontWeight: 300 }}
                  data-testid={`invoice-doc-line-title-${item.id}`}
                />
                <EditableText
                  value={item.description}
                  onChange={(v) => onLineChange(item.id, "description", v)}
                  placeholder="Description under the title"
                  multiline
                  style={{
                    fontSize: 11,
                    color: META,
                    fontStyle: "italic",
                    marginTop: 2,
                  }}
                  data-testid={`invoice-doc-line-description-${item.id}`}
                />
              </td>
              <td
                style={{
                  padding: "12px 0 10px",
                  borderBottom: `0.5px solid ${RULE_SOFT}`,
                  verticalAlign: "top",
                  textAlign: "right",
                }}
              >
                <div style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
                  <span style={{ fontSize: 13, fontWeight: 300, color: TEXT }}>$</span>
                  <EditableText
                    value={item.amount}
                    onChange={(v) => onLineChange(item.id, "amount", sanitizeMoneyInput(v))}
                    placeholder="0"
                    style={{
                      fontSize: 13,
                      fontWeight: 300,
                      textAlign: "right",
                      width: 88,
                      fontVariantNumeric: "tabular-nums",
                    }}
                    data-testid={`invoice-doc-line-amount-${item.id}`}
                  />
                </div>
              </td>
              <td
                style={{
                  padding: "8px 0 8px 4px",
                  borderBottom: `0.5px solid ${RULE_SOFT}`,
                  verticalAlign: "top",
                }}
              >
                {(lineItems || []).length > 1 ? (
                  <button
                    type="button"
                    onClick={() => onRemoveLine(item.id)}
                    aria-label={`Remove line ${index + 1}`}
                    style={{
                      border: "none",
                      background: "transparent",
                      color: META,
                      cursor: "pointer",
                      padding: 4,
                      display: "inline-flex",
                    }}
                    data-testid={`invoice-doc-remove-line-${item.id}`}
                  >
                    <Trash2 size={14} strokeWidth={1.4} />
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <button
        type="button"
        onClick={onAddLine}
        style={{
          marginTop: 10,
          border: "none",
          background: "transparent",
          color: ACCENT,
          fontFamily: FONT,
          fontWeight: 300,
          fontSize: 12,
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 0",
        }}
        data-testid="invoice-doc-add-line"
      >
        <Plus size={14} strokeWidth={1.4} />
        Add line
      </button>

      {/* Totals */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 18 }}>
        <div style={{ width: "42%", minWidth: 180 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "7px 0",
              borderBottom: `0.3px solid ${RULE_SOFT}`,
              fontSize: 12,
              fontWeight: 300,
            }}
          >
            <span>Subtotal</span>
            <span>{formatUsd(subtotal)}</span>
          </div>
          {collected > 0 ? (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "7px 0",
                borderBottom: `0.3px solid ${RULE_SOFT}`,
                fontSize: 12,
                fontWeight: 300,
                color: META,
              }}
            >
              <span>Paid</span>
              <span>-{formatUsd(collected)}</span>
            </div>
          ) : null}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              padding: "10px 0 0",
              marginTop: 2,
              borderTop: `0.5px solid ${TEXT}`,
              fontSize: 13,
              fontWeight: 300,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
              {collected > 0 && !isFullyPaid ? "Balance due" : "Total"}
              {isFullyPaid ? (
                <span
                  style={{
                    color: ACCENT,
                    fontSize: 11,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                  data-testid="invoice-doc-paid-badge"
                >
                  Paid
                </span>
              ) : null}
            </span>
            <span style={{ color: ACCENT }} data-testid="invoice-doc-total">
              {formatUsd(isFullyPaid ? 0 : collected > 0 ? balanceDue : subtotal)}
            </span>
          </div>
        </div>
      </div>

      {/* Payment */}
      <div
        style={{
          display: "flex",
          gap: 0,
          marginTop: 64,
        }}
      >
        <div
          style={{
            flex: 1,
            paddingRight: 20,
            borderRight: `0.3px solid ${RULE_SOFT}`,
          }}
        >
          <div
            style={{
              fontWeight: 300,
              fontSize: 9,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: META,
              borderBottom: `0.3px solid ${RULE_SOFT}`,
              paddingBottom: 5,
              marginBottom: 10,
            }}
          >
            Payment
          </div>
          <div style={{ fontSize: 12, fontWeight: 300, lineHeight: 1.7, color: TEXT }}>
            Please send payment directly via Apple Pay.
          </div>
          {showSchedule ? (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  fontWeight: 300,
                  fontSize: 9,
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                  color: META,
                  marginBottom: 6,
                }}
              >
                Payment schedule
              </div>
              {deposits.map((dep, i) => {
                const paid = isDepositPaid(dep);
                const label = dep.label || `Payment ${i + 1}`;
                const detail = [
                  formatUsd(dep.amount),
                  dep.due ? `due ${formatDisplayDate(dep.due)}` : "",
                ]
                  .filter(Boolean)
                  .join(" · ");
                const paidLabel = paid
                  ? dep.paid_at
                    ? `Paid · ${formatDisplayDate(dep.paid_at)}`
                    : "Paid"
                  : null;
                return (
                  <div
                    key={dep.id || i}
                    style={{
                      fontSize: 11,
                      fontWeight: 300,
                      color: paid ? META : TEXT,
                      padding: "5px 0",
                      borderBottom: `0.3px solid ${RULE_SOFT}`,
                      lineHeight: 1.5,
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: 10,
                    }}
                    data-testid={`invoice-doc-schedule-${dep.id || i}`}
                  >
                    <span
                      style={{
                        textDecoration: paid ? "line-through" : "none",
                      }}
                    >
                      {label}
                      {detail ? ` — ${detail}` : ""}
                    </span>
                    {paidLabel ? (
                      <span style={{ color: ACCENT, fontWeight: 500, whiteSpace: "nowrap" }}>
                        {paidLabel}
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              border: `0.5px solid ${TEXT}`,
              borderRadius: 4,
              padding: "6px 12px",
              marginTop: 10,
              fontWeight: 300,
              fontSize: 12,
              color: TEXT,
            }}
          >
            <span>Apple Pay</span>
            <span>857.399.3897</span>
          </div>
        </div>
        <div style={{ flex: 1, paddingLeft: 20 }}>
          <div
            style={{
              fontWeight: 300,
              fontSize: 9,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: META,
              borderBottom: `0.3px solid ${RULE_SOFT}`,
              paddingBottom: 5,
              marginBottom: 10,
            }}
          >
            Notes
          </div>
          <div style={{ fontSize: 12, fontWeight: 300, lineHeight: 1.7, color: TEXT }}>
            Sent w/ the most gratitude for you. &lt;3
          </div>
        </div>
      </div>
    </div>
  );
}
